import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  InputAdornment,
  Tabs,
  Tab,
  AppBar,
  Snackbar
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Key as KeyIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Code as CodeIcon,
  Analytics as AnalyticsIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Security as SecurityIcon,
  CloudUpload as CloudUploadIcon,
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  Launch as LaunchIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import api from '../api/axios';

const WebhooksTab = ({ companyId, projectId, project, isActive }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeSubTab, setActiveSubTab] = useState(0);
  
  // Main webhook settings state
  const [webhookSettings, setWebhookSettings] = useState({
    enabled: false,
    apiKeys: [],
    executeSettings: {
      allowFileUpload: true,
      maxFileSize: 50 * 1024 * 1024,
      allowedFileTypes: ['pdf', 'png', 'jpg', 'jpeg', 'xlsx', 'docx'],
      maxFilesPerRequest: 10,
      requireAuthentication: true,
      rateLimit: {
        enabled: true,
        requestsPerHour: 100,
        requestsPerDay: 1000
      }
    },
    updateSettings: {
      allowPostCompletionUpdates: true,
      allowInProgressUpdates: true,
      updateTimeWindow: 24 * 60 * 60 * 1000,
      allowedFields: ['result', 'variables', 'metadata']
    },
    security: {
      enforceHttps: true,
      ipWhitelist: [],
      requireUserAgent: false
    },
    analytics: {
      trackDetailedMetrics: true,
      retentionDays: 90
    },
    endpoints: {},
    statistics: {}
  });
  
  // API key management state
  const [apiKeyDialog, setApiKeyDialog] = useState(false);
  const [newApiKeyName, setNewApiKeyName] = useState('');
  const [newApiKeyPermissions, setNewApiKeyPermissions] = useState(['execute']);
  const [generatedApiKey, setGeneratedApiKey] = useState(null);
  const [showGeneratedKey, setShowGeneratedKey] = useState(false);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // Documentation state
  const [documentation, setDocumentation] = useState(null);
  const [docsLoading, setDocsLoading] = useState(false);
  
  // Copy notification state
  const [copyNotification, setCopyNotification] = useState(false);

  // Fetch webhook settings when tab becomes active
  useEffect(() => {
    if (isActive && companyId && projectId) {
      fetchWebhookSettings();
    }
  }, [isActive, companyId, projectId]);

  // Fetch analytics when analytics tab is selected
  useEffect(() => {
    if (isActive && activeSubTab === 1 && companyId && projectId) {
      fetchAnalytics();
    }
  }, [isActive, activeSubTab, companyId, projectId]);

  // Fetch documentation when docs tab is selected
  useEffect(() => {
    if (isActive && activeSubTab === 2 && companyId && projectId) {
      fetchDocumentation();
    }
  }, [isActive, activeSubTab, companyId, projectId]);

  const fetchWebhookSettings = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/webhook-settings`);
      setWebhookSettings(response.data);
      
      console.log('✅ Webhook settings loaded:', response.data);
    } catch (err) {
      console.error('❌ Error fetching webhook settings:', err);
      setError('Failed to load webhook settings');
    } finally {
      setLoading(false);
    }
  };

  const saveWebhookSettings = async () => {
    try {
      setSaving(true);
      setError('');
      
      await api.put(`/companies/${companyId}/projects/${projectId}/webhook-settings`, webhookSettings);
      
      setSuccess('Webhook settings saved successfully!');
      console.log('✅ Webhook settings saved');
      
      // Refresh settings to get updated endpoints
      fetchWebhookSettings();
    } catch (err) {
      console.error('❌ Error saving webhook settings:', err);
      setError('Failed to save webhook settings');
    } finally {
      setSaving(false);
    }
  };

  const generateApiKey = async () => {
    try {
      setSaving(true);
      setError('');
      
      const response = await api.post(`/companies/${companyId}/projects/${projectId}/webhook-api-keys`, {
        name: newApiKeyName,
        permissions: newApiKeyPermissions
      });
      
      setGeneratedApiKey(response.data);
      setSuccess('API key generated successfully! Make sure to copy it now - it won\'t be shown again.');
      
      // Reset form and refresh settings
      setNewApiKeyName('');
      setNewApiKeyPermissions(['execute']);
      fetchWebhookSettings();
    } catch (err) {
      console.error('❌ Error generating API key:', err);
      setError('Failed to generate API key');
    } finally {
      setSaving(false);
    }
  };

  const revokeApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      
      await api.delete(`/companies/${companyId}/projects/${projectId}/webhook-api-keys/${keyId}`);
      
      setSuccess('API key revoked successfully');
      fetchWebhookSettings();
    } catch (err) {
      console.error('❌ Error revoking API key:', err);
      setError('Failed to revoke API key');
    } finally {
      setSaving(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/webhook-analytics`);
      setAnalytics(response.data);
      
      console.log('✅ Webhook analytics loaded:', response.data);
    } catch (err) {
      console.error('❌ Error fetching analytics:', err);
      setAnalytics({ error: 'Failed to load analytics' });
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchDocumentation = async () => {
    try {
      setDocsLoading(true);
      
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/webhook-docs`);
      setDocumentation(response.data);
      
      console.log('✅ Webhook documentation loaded');
    } catch (err) {
      console.error('❌ Error fetching documentation:', err);
      setDocumentation({ error: 'Failed to load documentation' });
    } finally {
      setDocsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopyNotification(true);
  };

  const formatFileSize = (bytes) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (!isActive) {
    return null;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={40} />
        <Box ml={2}>
          <Typography variant="body2" color="textSecondary">
            Loading webhook settings...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Webhook Configuration
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={4}>
        Configure autonomous execution and post-completion update webhooks for external system integration.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Sub-tabs */}
      <AppBar position="static" color="transparent" elevation={0} sx={{ mb: 3 }}>
        <Tabs 
          value={activeSubTab} 
          onChange={(e, newValue) => setActiveSubTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<SettingsIcon />} label="Settings" />
          <Tab icon={<AnalyticsIcon />} label="Analytics" />
          <Tab icon={<DescriptionIcon />} label="Documentation" />
        </Tabs>
      </AppBar>

      {/* Settings Tab */}
      {activeSubTab === 0 && (
        <Box>
          {/* Main Enable/Disable */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Enable Webhook Execution
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Allow external systems to start executions and update results via API
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={webhookSettings.enabled}
                      onChange={(e) => setWebhookSettings(prev => ({
                        ...prev,
                        enabled: e.target.checked
                      }))}
                      color="primary"
                    />
                  }
                  label=""
                />
              </Box>
              
              {webhookSettings.enabled && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Webhook execution is enabled. External systems can now trigger executions using your API keys.
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>

          {webhookSettings.enabled && (
            <>
              {/* API Key Management */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight="bold">
                      <KeyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      API Keys
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setApiKeyDialog(true)}
                      disabled={saving}
                    >
                      Generate Key
                    </Button>
                  </Box>
                  
                  <Typography variant="body2" color="textSecondary" mb={3}>
                    API keys are used to authenticate webhook requests. Keep them secure!
                  </Typography>

                  {webhookSettings.apiKeys && webhookSettings.apiKeys.length > 0 ? (
                    <List>
                      {webhookSettings.apiKeys.map((apiKey, index) => (
                        <ListItem key={apiKey.keyId} divider={index < webhookSettings.apiKeys.length - 1}>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle1" fontWeight="medium">
                                  {apiKey.name}
                                </Typography>
                                <Chip label={apiKey.keyId} size="small" variant="outlined" />
                                {apiKey.permissions?.map(perm => (
                                  <Chip key={perm} label={perm} size="small" color="primary" />
                                ))}
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="textSecondary">
                                  Created: {new Date(apiKey.createdAt).toLocaleDateString()}
                                  {apiKey.lastUsedAt && (
                                    <> • Last used: {new Date(apiKey.lastUsedAt).toLocaleDateString()}</>
                                  )}
                                </Typography>
                                {apiKey.statistics && (
                                  <Typography variant="caption" color="textSecondary">
                                    {apiKey.statistics.totalRequests} requests • {apiKey.statistics.totalExecutions} executions
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              color="error"
                              onClick={() => revokeApiKey(apiKey.keyId)}
                              disabled={saving}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Alert severity="info">
                      No API keys configured. Generate your first API key to enable webhook execution.
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Webhook Endpoints */}
              {webhookSettings.endpoints && Object.keys(webhookSettings.endpoints).length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" mb={2}>
                      <LaunchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Webhook Endpoints
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Execution Endpoint"
                          value={webhookSettings.endpoints.executeUrl || ''}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => copyToClipboard(webhookSettings.endpoints.executeUrl)}
                                  edge="end"
                                >
                                  <ContentCopyIcon />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Update Endpoint Template"
                          value={webhookSettings.endpoints.updateUrlTemplate || ''}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => copyToClipboard(webhookSettings.endpoints.updateUrlTemplate)}
                                  edge="end"
                                >
                                  <ContentCopyIcon />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Execution Settings */}
              <Accordion sx={{ mb: 3 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight="bold">
                    <CloudUploadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Execution Settings
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={webhookSettings.executeSettings.allowFileUpload}
                            onChange={(e) => setWebhookSettings(prev => ({
                              ...prev,
                              executeSettings: {
                                ...prev.executeSettings,
                                allowFileUpload: e.target.checked
                              }
                            }))}
                          />
                        }
                        label="Allow File Upload"
                      />
                    </Grid>
                    
                    {webhookSettings.executeSettings.allowFileUpload && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Max File Size"
                            value={formatFileSize(webhookSettings.executeSettings.maxFileSize)}
                            InputProps={{ readOnly: true }}
                            size="small"
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Max Files Per Request"
                            type="number"
                            value={webhookSettings.executeSettings.maxFilesPerRequest}
                            onChange={(e) => setWebhookSettings(prev => ({
                              ...prev,
                              executeSettings: {
                                ...prev.executeSettings,
                                maxFilesPerRequest: parseInt(e.target.value) || 1
                              }
                            }))}
                            size="small"
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" mb={1}>Allowed File Types</Typography>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            {webhookSettings.executeSettings.allowedFileTypes.map(type => (
                              <Chip key={type} label={type.toUpperCase()} size="small" />
                            ))}
                          </Box>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Update Settings */}
              <Accordion sx={{ mb: 3 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight="bold">
                    <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Post-Completion Update Settings
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={webhookSettings.updateSettings.allowPostCompletionUpdates}
                            onChange={(e) => setWebhookSettings(prev => ({
                              ...prev,
                              updateSettings: {
                                ...prev.updateSettings,
                                allowPostCompletionUpdates: e.target.checked
                              }
                            }))}
                          />
                        }
                        label="Allow Post-Completion Updates"
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={webhookSettings.updateSettings.allowInProgressUpdates}
                            onChange={(e) => setWebhookSettings(prev => ({
                              ...prev,
                              updateSettings: {
                                ...prev.updateSettings,
                                allowInProgressUpdates: e.target.checked
                              }
                            }))}
                          />
                        }
                        label="Allow In-Progress Updates"
                      />
                      <Typography variant="caption" color="textSecondary" display="block">
                        Allow updates while execution is running (in_progress, waiting_input, etc.)
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                        ⚡ This setting is enabled by default to allow real-time updates during workflow execution
                      </Typography>
                    </Grid>
                    
                    {webhookSettings.updateSettings.allowPostCompletionUpdates && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Update Time Window (hours)"
                            type="number"
                            value={Math.round(webhookSettings.updateSettings.updateTimeWindow / (60 * 60 * 1000))}
                            onChange={(e) => setWebhookSettings(prev => ({
                              ...prev,
                              updateSettings: {
                                ...prev.updateSettings,
                                updateTimeWindow: (parseInt(e.target.value) || 24) * 60 * 60 * 1000
                              }
                            }))}
                            size="small"
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" mb={1}>Allowed Update Fields</Typography>
                          <Typography variant="caption" color="textSecondary" mb={2} display="block">
                            Select which types of data can be updated via webhook after execution completion
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            {['result', 'variables', 'metadata', 'customData'].map(field => {
                              const isSelected = webhookSettings.updateSettings.allowedFields.includes(field);
                              return (
                                <Chip
                                  key={field}
                                  label={field}
                                  size="small"
                                  color={isSelected ? "primary" : "default"}
                                  variant={isSelected ? "filled" : "outlined"}
                                  clickable
                                  onClick={() => {
                                    const currentFields = webhookSettings.updateSettings.allowedFields;
                                    const newFields = isSelected
                                      ? currentFields.filter(f => f !== field)
                                      : [...currentFields, field];
                                    
                                    setWebhookSettings(prev => ({
                                      ...prev,
                                      updateSettings: {
                                        ...prev.updateSettings,
                                        allowedFields: newFields
                                      }
                                    }));
                                  }}
                                  icon={isSelected ? <CheckIcon /> : undefined}
                                />
                              );
                            })}
                          </Box>
                          <Typography variant="caption" color="textSecondary" mt={1} display="block">
                            • <strong>result</strong>: Final execution results/output data<br/>
                            • <strong>variables</strong>: Flow execution variables<br/>
                            • <strong>metadata</strong>: Execution metadata and system info<br/>
                            • <strong>customData</strong>: Custom external data fields
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Rate Limiting */}
              <Accordion sx={{ mb: 3 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight="bold">
                    <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Rate Limiting & Security
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={webhookSettings.executeSettings.rateLimit?.enabled}
                            onChange={(e) => setWebhookSettings(prev => ({
                              ...prev,
                              executeSettings: {
                                ...prev.executeSettings,
                                rateLimit: {
                                  ...prev.executeSettings.rateLimit,
                                  enabled: e.target.checked
                                }
                              }
                            }))}
                          />
                        }
                        label="Enable Rate Limiting"
                      />
                    </Grid>
                    
                    {webhookSettings.executeSettings.rateLimit?.enabled && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Requests Per Hour"
                            type="number"
                            value={webhookSettings.executeSettings.rateLimit.requestsPerHour}
                            onChange={(e) => setWebhookSettings(prev => ({
                              ...prev,
                              executeSettings: {
                                ...prev.executeSettings,
                                rateLimit: {
                                  ...prev.executeSettings.rateLimit,
                                  requestsPerHour: parseInt(e.target.value) || 100
                                }
                              }
                            }))}
                            size="small"
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Requests Per Day"
                            type="number"
                            value={webhookSettings.executeSettings.rateLimit.requestsPerDay}
                            onChange={(e) => setWebhookSettings(prev => ({
                              ...prev,
                              executeSettings: {
                                ...prev.executeSettings,
                                rateLimit: {
                                  ...prev.executeSettings.rateLimit,
                                  requestsPerDay: parseInt(e.target.value) || 1000
                                }
                              }
                            }))}
                            size="small"
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Save Button */}
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  onClick={fetchWebhookSettings}
                  disabled={saving}
                >
                  Reset Changes
                </Button>
                <Button
                  variant="contained"
                  onClick={saveWebhookSettings}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} /> : null}
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Analytics Tab */}
      {activeSubTab === 1 && (
        <Box>
          {analyticsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="textSecondary" ml={2}>
                Loading analytics...
              </Typography>
            </Box>
          ) : analytics?.error ? (
            <Alert severity="error">{analytics.error}</Alert>
          ) : analytics ? (
            <Grid container spacing={3}>
              {/* Overview Cards */}
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      {analytics.overview?.totalExecutions || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Executions
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h4" fontWeight="bold" color="success.main">
                      {analytics.overview?.successfulExecutions || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Successful
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h4" fontWeight="bold" color="warning.main">
                      {analytics.overview?.successRate || 0}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Success Rate
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h4" fontWeight="bold" color="info.main">
                      {analytics.overview?.totalApiCalls || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      API Calls
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* API Keys List */}
              {analytics.apiKeys && analytics.apiKeys.length > 0 && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" mb={2}>
                        API Key Usage
                      </Typography>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Key Name</TableCell>
                              <TableCell>Permissions</TableCell>
                              <TableCell>Total Requests</TableCell>
                              <TableCell>Executions</TableCell>
                              <TableCell>Last Used</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {analytics.apiKeys.map((key) => (
                              <TableRow key={key.keyId}>
                                <TableCell>{key.name}</TableCell>
                                <TableCell>
                                  {key.permissions.map(perm => (
                                    <Chip key={perm} label={perm} size="small" sx={{ mr: 0.5 }} />
                                  ))}
                                </TableCell>
                                <TableCell>{key.statistics?.totalRequests || 0}</TableCell>
                                <TableCell>{key.statistics?.totalExecutions || 0}</TableCell>
                                <TableCell>
                                  {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleDateString() : 'Never'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          ) : (
            <Alert severity="info">
              No webhook analytics available. Enable webhooks and generate API keys to see usage data.
            </Alert>
          )}
        </Box>
      )}

      {/* Documentation Tab */}
      {activeSubTab === 2 && (
        <Box>
          {docsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="textSecondary" ml={2}>
                Loading documentation...
              </Typography>
            </Box>
          ) : documentation?.error ? (
            <Alert severity="error">{documentation.error}</Alert>
          ) : documentation ? (
            <Box>
              {/* Quick Start */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" mb={2}>
                    <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Quick Start
                  </Typography>
                  
                  <Typography variant="body2" color="textSecondary" mb={3}>
                    Get started with webhook execution in just a few steps:
                  </Typography>
                  
                  <Box component="ol" sx={{ pl: 3 }}>
                    <Typography component="li" variant="body2" mb={1}>
                      Generate an API key in the Settings tab
                    </Typography>
                    <Typography component="li" variant="body2" mb={1}>
                      Use the execution endpoint to start flows with data and files
                    </Typography>
                    <Typography component="li" variant="body2" mb={1}>
                      Optionally update execution results using the update endpoint
                    </Typography>
                    <Typography component="li" variant="body2">
                      Monitor execution status and retrieve results via the status/results endpoints
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Endpoints */}
              <Accordion sx={{ mb: 3 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight="bold">
                    API Endpoints
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                          Start Execution
                        </Typography>
                        <Typography variant="body2" color="textSecondary" mb={1}>
                          POST {documentation.endpoints?.execute}
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => copyToClipboard(documentation.endpoints?.execute)}
                        >
                          Copy URL
                        </Button>
                      </Paper>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                          Update Results
                        </Typography>
                        <Typography variant="body2" color="textSecondary" mb={1}>
                          POST {documentation.endpoints?.updateTemplate}
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => copyToClipboard(documentation.endpoints?.updateTemplate)}
                        >
                          Copy URL
                        </Button>
                      </Paper>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Code Examples */}
              <Accordion sx={{ mb: 3 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight="bold">
                    <CodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Code Examples
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                      cURL Example - Start Execution
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'white', mb: 3 }}>
                      <Typography component="pre" variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                        {documentation.executeEndpoint?.curlExample}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => copyToClipboard(documentation.executeEndpoint?.curlExample)}
                        sx={{ mt: 1, color: 'white' }}
                      >
                        Copy
                      </Button>
                    </Paper>
                    
                    <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                      JavaScript SDK Example
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'white' }}>
                      <Typography component="pre" variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                        {documentation.sdkExamples?.javascript}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => copyToClipboard(documentation.sdkExamples?.javascript)}
                        sx={{ mt: 1, color: 'white' }}
                      >
                        Copy
                      </Button>
                    </Paper>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Settings Summary */}
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" mb={2}>
                    Current Configuration
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="textSecondary">File Upload</Typography>
                      <Typography variant="body1">
                        {documentation.settings?.fileUpload ? 
                          <Chip icon={<CheckCircleIcon />} label="Enabled" color="success" size="small" /> :
                          <Chip icon={<ErrorIcon />} label="Disabled" color="error" size="small" />
                        }
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="textSecondary">Max File Size</Typography>
                      <Typography variant="body1">
                        {formatFileSize(documentation.settings?.maxFileSize || 0)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="textSecondary">Rate Limit</Typography>
                      <Typography variant="body1">
                        {documentation.settings?.rateLimit?.requestsPerHour}/hour, {documentation.settings?.rateLimit?.requestsPerDay}/day
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Allowed File Types</Typography>
                      <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                        {documentation.settings?.allowedFileTypes?.map(type => (
                          <Chip key={type} label={type.toUpperCase()} size="small" />
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          ) : (
            <Alert severity="info">
              Enable webhooks to view documentation and examples.
            </Alert>
          )}
        </Box>
      )}

      {/* API Key Generation Dialog */}
      <Dialog open={apiKeyDialog} onClose={() => setApiKeyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate New API Key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Key Name"
            fullWidth
            variant="outlined"
            value={newApiKeyName}
            onChange={(e) => setNewApiKeyName(e.target.value)}
            placeholder="e.g., Production API Key"
            sx={{ mb: 2 }}
          />
          
          <FormControl fullWidth variant="outlined">
            <InputLabel>Permissions</InputLabel>
            <Select
              multiple
              value={newApiKeyPermissions}
              onChange={(e) => setNewApiKeyPermissions(e.target.value)}
              label="Permissions"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              <MenuItem value="execute">Execute (start flows)</MenuItem>
              <MenuItem value="update">Update (modify results)</MenuItem>
              <MenuItem value="read">Read (view status/results)</MenuItem>
              <MenuItem value="admin">Admin (full access)</MenuItem>
            </Select>
          </FormControl>

          {generatedApiKey && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                API Key Generated Successfully!
              </Typography>
              <Typography variant="body2" mb={2}>
                Copy this key now - it won't be shown again:
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  fullWidth
                  value={generatedApiKey.plainKey}
                  type={showGeneratedKey ? 'text' : 'password'}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowGeneratedKey(!showGeneratedKey)}
                          edge="end"
                        >
                          {showGeneratedKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                />
                <Button
                  variant="outlined"
                  onClick={() => copyToClipboard(generatedApiKey.plainKey)}
                  startIcon={<ContentCopyIcon />}
                >
                  Copy
                </Button>
              </Box>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setApiKeyDialog(false);
            setGeneratedApiKey(null);
            setShowGeneratedKey(false);
          }}>
            {generatedApiKey ? 'Close' : 'Cancel'}
          </Button>
          {!generatedApiKey && (
            <Button
              onClick={generateApiKey}
              variant="contained"
              disabled={!newApiKeyName.trim() || saving}
              startIcon={saving ? <CircularProgress size={20} /> : <KeyIcon />}
            >
              {saving ? 'Generating...' : 'Generate Key'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Copy notification */}
      <Snackbar
        open={copyNotification}
        autoHideDuration={2000}
        onClose={() => setCopyNotification(false)}
        message="Copied to clipboard!"
      />
    </Box>
  );
};

export default WebhooksTab;
